import { useEffect, useState } from "react";

import { useLazyQuery } from "@apollo/client/react";

import {
  ConversationDraftWordDocumentDocument,
  ProjectDraftingWordDocumentDocument,
} from "@app_schema";

import { track } from "@application/utilities/track";

const WORD_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const FAILED_MS = 4000;
const REVOKE_MS = 1000;

// Downloads his Word draft with the chat's sections typed in, or before any chat his edits only
// (`projectId`). The server builds it on the click, never cached or polled. A failure shows on the
// button for a few seconds.
export function useDraftDownload({
  conversationId,
  projectId,
}: {
  conversationId?: string;
  projectId: string;
}) {
  const [fetchChat, chatState] = useLazyQuery(
    ConversationDraftWordDocumentDocument,
    { fetchPolicy: "no-cache" },
  );
  const [fetchCase, caseState] = useLazyQuery(
    ProjectDraftingWordDocumentDocument,
    { fetchPolicy: "no-cache" },
  );
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!failed) return;
    const timeout = setTimeout(() => setFailed(false), FAILED_MS);
    return () => clearTimeout(timeout);
  }, [failed]);

  const download = async () => {
    setFailed(false);
    const word = await (
      conversationId
        ? fetchChat({ variables: { id: conversationId } }).then(
            ({ data, error }) =>
              error ? null : data?.conversation.draftWordDocument,
          )
        : fetchCase({ variables: { id: projectId } }).then(({ data, error }) =>
            error ? null : data?.project.draftingWordDocument,
          )
    ).catch(() => null);
    if (!word) {
      setFailed(true);
      return;
    }

    const bytes = Uint8Array.from(atob(word.content), (char) =>
      char.charCodeAt(0),
    );
    const url = URL.createObjectURL(new Blob([bytes], { type: WORD_TYPE }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = word.filename;
    anchor.click();
    // Firefox and Safari cancel the download if the URL goes at once.
    setTimeout(() => URL.revokeObjectURL(url), REVOKE_MS);
    track("document_downloaded", {
      project_id: projectId,
      kind: "drafting_report",
      source: conversationId ? "chat" : "case",
    });
  };

  const downloading = chatState.loading || caseState.loading;
  return { download, downloading, failed } as const;
}
